/**
 * @file: messageListShared.tsx
 *
 * Shared contract for the message-list backends (FlatList / FlashList). The
 * conversation talks to a list only through `MessageListProps` + the minimal
 * `MessageListRef` (the scroll affordances `useConversationScroll` drives), so
 * the concrete list is swappable — see `MessageList.tsx`.
 */

import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ChatMsg } from '@euler/model/chat';
import { FC, RefObject, memo } from 'react';
import {
  ActivityIndicator,
  ListRenderItem,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { BehaviorSubject } from 'rxjs';

/** The scroll surface `useConversationScroll` commands — satisfied
 * structurally by both `FlatList` and `FlashList` instances. */
export interface MessageListRef {
  scrollToIndex(params: {
    index: number;
    animated?: boolean;
    viewPosition?: number;
    viewOffset?: number;
  }): void;
  scrollToOffset(params: { offset: number; animated?: boolean }): void;
}

export interface MessageListProps {
  messages: ChatMsg[];
  renderItem: ListRenderItem<ChatMsg>;
  /** Bound to `useConversationScroll`'s ref (named for historical reasons). */
  flatListRef: RefObject<MessageListRef>;
  onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void;
  /** FlatList-only; FlashList resolves a failed scroll-to-index itself. */
  onScrollToIndexFailed: (info: { index: number }) => void;
  onEndReached: () => void;
  loadingOlder$: BehaviorSubject<boolean>;
  style?: StyleProp<ViewStyle>;
}

export const MESSAGE_LIST_BG = '#fafafa';

// Rendered at the END of the inverted list = the visual TOP, while an older
// page loads.
export const LoadingOlderFooter: FC = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ paddingVertical: rpx(12), alignItems: 'center' }}>
      <ActivityIndicator size="small" color="#9ca3af" />
    </View>
  );
});
